import { useState, useEffect, useRef } from 'react';

interface Props {
  analysisType: string;
  /** Progression réelle 0-100 émise par le backend en SSE. null = pas encore reçu. */
  progress?: number | null;
  /** Label correspondant à l'étape en cours côté backend. */
  progressLabel?: string | null;
}

// Fallback : steps animés en boucle si on n'a pas encore reçu d'event SSE.
const ANALYSIS_STEPS_FALLBACK = [
  'Analyse du contenu en cours...',
  'Extraction des éléments factuels...',
  'Recherche dans la base juridique...',
  'Évaluation par l\'avocat de la défense...',
  'Évaluation par le contradicteur...',
  'Arbitrage et pondération en cours...',
  'Rédaction du verdict final...',
];

const QUESTION_STEPS_FALLBACK = [
  'Recherche dans la base juridique...',
  'Consultation des textes de loi...',
  'Analyse de la jurisprudence...',
  'Rédaction de la réponse...',
];

const CREATIVE_STEPS_FALLBACK = [
  'Analyse du brief créatif...',
  'Recherche des campagnes conformes...',
  'Consultation du cadre légal...',
  'Brainstorm des territoires créatifs...',
  'Exploration des angles originaux...',
  'Rédaction des slogans et concepts...',
  'Validation juridique de chaque idée...',
  'Scoring de conformité...',
];

export default function Loader({ analysisType, progress = null, progressLabel = null }: Props) {
  const isQuestion = analysisType === 'Question générale';
  const isCreative = analysisType === 'Évin Créa';
  const fallbackSteps = isCreative
    ? CREATIVE_STEPS_FALLBACK
    : isQuestion
    ? QUESTION_STEPS_FALLBACK
    : ANALYSIS_STEPS_FALLBACK;
  const [currentStep, setCurrentStep] = useState(0);

  // Animation legacy : tourne uniquement quand on n'a pas encore reçu de progression SSE.
  useEffect(() => {
    if (progress !== null) return;
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % fallbackSteps.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [fallbackSteps.length, progress]);

  // ── Jauge « trickle » ────────────────────────────────────
  // Le backend remonte la progression par paliers (ex. 38 puis 80). Entre deux
  // paliers, on fait grimper la barre en asymptote vers un plafond (real+40,
  // borné à 95) pour montrer que l'IA cogite — sans jamais atteindre 100 avant
  // le vrai 'done'. On saute toujours en avant quand un vrai palier arrive.
  const [displayed, setDisplayed] = useState<number | null>(null);
  const realRef = useRef(0);
  const started = progress !== null;

  useEffect(() => {
    if (progress === null) { setDisplayed(null); return; }
    realRef.current = Math.max(0, Math.min(100, progress));
    setDisplayed((d) => Math.max(d ?? 0, realRef.current));
  }, [progress]);

  useEffect(() => {
    if (!started) return;
    const id = setInterval(() => {
      setDisplayed((d) => {
        const real = realRef.current;
        if (real >= 100) return 100;
        const cur = d ?? real;
        // Deux régimes : phase « juges » (mid) → on couvre gentiment le gros
        // saut 38→80 ; phase « arbitrage » (real ≥ 70, qui dure 1-2 min) → on
        // avance TRÈS lentement en asymptote vers 95, pour un mouvement continu
        // qui ne donne jamais l'impression d'être figé près de 100.
        const isLate = real >= 70;
        const cap = isLate ? 95 : Math.min(real + 38, 90);
        const factor = isLate ? 0.006 : 0.03;
        if (cur >= cap) return cur;
        return cur + (cap - cur) * factor;
      });
    }, 250);
    return () => clearInterval(id);
  }, [started]);

  // Affichage : on privilégie l'étape backend si dispo, sinon fallback animé.
  const liveLabel = progressLabel ?? fallbackSteps[currentStep];
  const pct = displayed;

  return (
    /* Mode pastel : panneau doux teinté par la typologie (`p-card-tint`) —
       pendant l'attente, la couleur reste le seul repère de « où l'on est ». */
    <div className="p-card-tint animate-fade-in flex flex-col items-center justify-center py-20 pastel:mx-auto pastel:max-w-2xl pastel:px-6 pastel:py-16">
      {/* Spinner — double cercle tournant autour du badge Evinly fixe.
          En pastel, les arcs reprennent l'encre du cadre et la teinte de la typologie. */}
      <div className="relative w-20 h-20 mb-8">
        <div className="absolute inset-0 rounded-full border-2 border-slate-200 dark:border-slate-700 shader:border-white/10 pastel:border-(--tint-strong)/25" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-violet-600 dark:border-t-white shader:border-t-white pastel:border-t-(--p-ink) animate-spin" />
        <div className="absolute inset-3 rounded-full border-2 border-transparent border-b-indigo-500 dark:border-b-white shader:border-b-white pastel:border-b-(--tint-strong) animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        {/* Badge Evinly centré — respiration + petit tour « bouncy » périodique */}
        <div className="absolute inset-0 flex items-center justify-center animate-evinly-mark">
          <img
            src="/evinly_logo_blue.svg"
            alt=""
            aria-hidden="true"
            className="w-9 h-9 select-none block dark:hidden shader:hidden"
            draggable={false}
          />
          <img
            src="/evinly_logo.svg"
            alt=""
            aria-hidden="true"
            className="w-9 h-9 select-none hidden dark:block shader:block"
            draggable={false}
          />
        </div>
      </div>

      <h3 className="font-display text-lg text-slate-900 dark:text-white mb-2">
        Analyse &laquo;&nbsp;{analysisType}&nbsp;&raquo;
      </h3>

      <p className="text-sm text-violet-600 dark:text-violet-400 shader:text-white/90 h-5 transition-opacity">
        {liveLabel}
      </p>

      {/* Vraie jauge SSE quand le backend a émis au moins un event ; sinon pastilles legacy */}
      {pct !== null ? (
        <div className="mt-6 w-full max-w-sm">
          <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 shader:bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 dark:from-white dark:to-white shader:from-white shader:to-white transition-[width] duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 text-right tabular-nums">
            {Math.round(pct)}%
          </div>
        </div>
      ) : (
        <div className="mt-8 flex gap-1.5">
          {fallbackSteps.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                i <= currentStep
                  ? 'bg-violet-500 dark:bg-white shader:bg-white'
                  : 'bg-slate-200 dark:bg-slate-700 shader:bg-white/10'
              }`}
            />
          ))}
        </div>
      )}

      <p className="mt-6 text-xs text-slate-400 dark:text-slate-500 shader:text-white/50 max-w-sm text-center">
        {isCreative
          ? 'Notre IA explore des territoires créatifs puis les valide juridiquement. Merci de patienter, l\'analyse est en cours.'
          : isQuestion
          ? 'Notre IA recherche dans la base juridique et rédige la réponse. Merci de patienter, l\'analyse est en cours.'
          : 'L\'analyse mobilise nos 3 modèles d\'IA en séquence pour garantir un verdict équilibré et nuancé. Merci de patienter, l\'analyse est en cours.'}
      </p>

      <div className="mt-4 flex items-start gap-2 max-w-sm text-center mx-auto">
        <svg className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
        </svg>
        <p className="text-xs text-slate-400 dark:text-slate-500 shader:text-white/50 text-left">
          Vous pouvez quitter cet écran : l'analyse continue en arrière-plan et vous serez notifié dès qu'elle est prête. Aucun crédit supplémentaire ne sera débité.
        </p>
      </div>
    </div>
  );
}
